export interface ActivePackage {
  id: string;
  name: string;
  description: string;
  durationMonths: number;
  price: number;
  offerPrice: number;
  nriPrice: number;
  currency: string;
  mobileNumberAccessCount: number;
  sendMessageCount: number;
  memberChat: boolean;
  viewHoroscope: boolean;
  chatUsersCount: number;
  horoscopeCount: number;
  isAudioCalling: boolean;
  isVideoCalling: boolean;
  audioCallingMins: number;
  videoCallingMins: number;
  personalRelationshipManager: boolean;
  expertProfileWritingService: boolean;
  instantQuickCallSupport: boolean;
  assistedDashboard: boolean;
  sortOrder: number;
  publiclyAvailable: boolean;
}

function readNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function readBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1';
  }
  if (typeof value === 'number') return value !== 0;
  return fallback;
}

function readNestedFeatures(raw: Record<string, unknown>): Record<string, unknown> | null {
  const features = raw.features;
  if (features && typeof features === 'object') {
    return features as Record<string, unknown>;
  }
  return null;
}

export function normalizePackage(raw: Record<string, unknown>): ActivePackage {
  const features = readNestedFeatures(raw);

  const pick = <T,>(key: string, snakeKey: string, reader: (v: unknown) => T, fallback: T): T => {
    if (raw[key] !== undefined && raw[key] !== null) return reader(raw[key]);
    if (raw[snakeKey] !== undefined && raw[snakeKey] !== null) return reader(raw[snakeKey]);
    if (features?.[key] !== undefined && features[key] !== null) return reader(features[key]);
    if (features?.[snakeKey] !== undefined && features[snakeKey] !== null) {
      return reader(features[snakeKey]);
    }
    return fallback;
  };

  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    durationMonths: pick('durationMonths', 'duration_months', readNumber, 12),
    price: pick('price', 'price', readNumber, 0),
    offerPrice: pick('offerPrice', 'offer_price', readNumber, 0),
    nriPrice: pick('nriPrice', 'nri_price', readNumber, 0),
    currency: String(raw.currency ?? 'INR'),
    mobileNumberAccessCount: pick(
      'mobileNumberAccessCount',
      'mobile_number_access_count',
      readNumber,
      0
    ),
    sendMessageCount: pick('sendMessageCount', 'send_message_count', readNumber, 0),
    memberChat: pick('memberChat', 'member_chat', readBoolean, false),
    viewHoroscope: pick('viewHoroscope', 'view_horoscope', readBoolean, false),
    chatUsersCount: pick('chatUsersCount', 'chat_users_count', readNumber, 0),
    horoscopeCount: pick('horoscopeCount', 'horoscope_count', readNumber, 0),
    isAudioCalling: pick('isAudioCalling', 'is_audio_calling', readBoolean, false),
    isVideoCalling: pick('isVideoCalling', 'is_video_calling', readBoolean, false),
    audioCallingMins: pick('audioCallingMins', 'audio_calling_mins', readNumber, 0),
    videoCallingMins: pick('videoCallingMins', 'video_calling_mins', readNumber, 0),
    personalRelationshipManager: pick(
      'personalRelationshipManager',
      'personal_relationship_manager',
      readBoolean,
      false
    ),
    expertProfileWritingService: pick(
      'expertProfileWritingService',
      'expert_profile_writing_service',
      readBoolean,
      false
    ),
    instantQuickCallSupport: pick(
      'instantQuickCallSupport',
      'instant_quick_call_support',
      readBoolean,
      false
    ),
    assistedDashboard: pick('assistedDashboard', 'assisted_dashboard', readBoolean, false),
    sortOrder: pick('sortOrder', 'sort_order', readNumber, 0),
    publiclyAvailable: pick('publiclyAvailable', 'publicly_available', readBoolean, true),
  };
}

export function sortActivePackages(packages: ActivePackage[]): ActivePackage[] {
  return [...packages]
    .filter((pkg) => pkg.id)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.durationMonths - b.durationMonths);
}
